/**
 * Client-side certificate rendering.
 *
 * Uses the *production* Handlebars template and CSS shipped in
 * `packages/brand/certificate/` — the exact same files the NestJS API renders
 * server-side with Chromium. One design, one source of truth.
 */
import Handlebars from "handlebars";
import QRCode from "qrcode";

import templateSrc from "@brand/certificate/certificate.hbs?raw";
import certificateCss from "@brand/certificate/certificate.css?raw";
import i18n from "@brand/certificate/i18n.json";
import markSvg from "@brand/assets/logos/versus-mark-black.svg?raw";
import mediterranean from "@brand/src/mediterranean.json";

import type { Locale, SurfaceRecord } from "./types";
import { guarantor } from "./config";

export type CertificateTheme = "glass" | "heritage";

const template = Handlebars.compile(templateSrc);

/**
 * The master mark ships as a single-fill SVG. Strip its classes/styles so paths
 * inherit `fill` from the root, letting one source be tinted gold or violet.
 */
export function tintSvg(svg: string, color: string) {
  return svg
    .replace(/<style[\s\S]*?<\/style>/g, "")
    .replace(/\sfill="[^"]*"/g, "")
    .replace(/fill:[^;}"']+/g, "")
    .replace(/\sclass="[^"]*"/g, "")
    .replace(/<svg\b/, `<svg fill="${color}"`);
}

export interface MedColor {
  name: string;
  hex: string;
  status: string;
}

export const medColors: MedColor[] = mediterranean.groups.flatMap((g) => g.colors);

export function findMedColor(name: string | null | undefined): MedColor | undefined {
  if (!name) return undefined;
  const needle = name.trim().toLowerCase();
  return medColors.find((c) => c.name.toLowerCase() === needle);
}

export function labelsFor(locale: Locale) {
  return i18n[locale].labels as Record<string, string | string[]>;
}

export async function renderCertificateHtml(
  record: SurfaceRecord,
  locale: Locale,
  theme: CertificateTheme = "glass",
): Promise<string> {
  const dict = i18n[locale];
  const t = (group: keyof typeof dict, key: string) =>
    (dict[group] as Record<string, string>)[key] ?? key;

  const qrDataUri = await QRCode.toDataURL(record.verifyUrl, {
    margin: 0,
    width: 420,
    color: { dark: "#120a22", light: "#ffffff" },
    errorCorrectionLevel: "M",
  });

  const medallionSvg = tintSvg(markSvg, "currentColor");
  const sealSvg = tintSvg(markSvg, "currentColor");

  const color = findMedColor(record.surface.colorRef);

  const context = {
    theme,
    locale,
    labels: dict.labels,
    certificate: {
      number: record.serial,
      revision: String(record.revision).padStart(2, "0"),
      issuedAt: record.issuedAt,
      status: record.status,
      statusLabel: t("status", record.status),
      reviewer: record.reviewer,
      insurancePolicyRef: record.insurancePolicyRef ?? "",
    },
    project: {
      client: record.project.client,
      siteAddress: record.project.siteAddress,
      city: record.project.city,
    },
    surface: {
      name: record.surface.name,
      m2: record.surface.m2,
      typeLabel: record.surface.typeLabel,
      location: record.surface.location ?? "",
      systemLabel: record.surface.systemLabel,
      finishLabel: record.surface.finishLabel,
      colorRef: record.surface.colorRef ?? "",
      colorSwatch: color?.hex ?? record.surface.colorSwatch ?? null,
      customDesign: record.surface.customDesign ?? "",
    },
    lots: record.lots,
    applicator: record.applicator,
    dates: record.dates,
    warranty: record.warranty.map((w) => ({ ...w, label: t("warranty", w.kind) })),
    qr: { dataUri: qrDataUri, url: record.verifyUrl },
    guarantor,
    brand: { medallionSvg, sealSvg },
  };

  return template({ ...context, css: certificateCss });
}
